/**
 * Dossiê do cliente: uma página só, sem abas, com o que importa daquele cliente.
 *
 * Ordem (plano do dossiê, 29/10/2026): cabeçalho com contato e ações, casos,
 * prazos e tarefas, financeiro, documentos e, recolhidos, atendimentos e
 * histórico. Cada bloco carrega e falha sozinho: um erro nos atendimentos
 * não derruba a página. Dados de cadastro (CPF, nº do cliente, etiquetas)
 * ficam em "Editar cadastro".
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Building2, ChevronDown, Edit, Mail, MessageSquare, Plus, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import { WhatsAppLink } from '@/components/common/WhatsAppLink'
import { ClientCaseList } from '@/components/cases/ClientCaseList'
import { InteractionTimelineCard } from '@/components/InteractionTimelineCard'
import { ClientTimeline } from '@/components/timeline/ClientTimeline'
import { TaskListByClient } from '@/components/tasks/TaskListByClient'
import { TaskQuickCreateButton } from '@/components/tasks/TaskQuickCreateButton'
import { ClientDocumentsAggregated } from '@/components/documents/ClientDocumentsAggregated'
import { FinanceSummaryCard } from '@/components/finance-v2/FinanceSummaryCard'
import { clientService } from '@/services/client-service'
import { caseService } from '@/services/case-service'
import { rememberClient } from '@/lib/recent-clients'
import { Client } from '@/types/client'
import { Case } from '@/types/case'
import { getErrorMessage, getErrorStatus } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'
import { cn } from '@/utils/cn'

type InteractionType =
  | 'meeting' | 'phone_call' | 'email' | 'whatsapp' | 'consultation'
  | 'document_review' | 'follow_up' | 'initial_contact'
  | 'court_appearance' | 'negotiation' | 'other'

interface Interaction {
  id: string
  clientId: string
  type: InteractionType
  subject: string
  description?: string
  createdAt: string
  completedAt?: string
  tags: string[]
  createdBy: string
  linkedCaseId?: string
  linkedCaseNumber?: string
  linkedProcessId?: string
  linkedProcessNumber?: string
}

type Load<T> = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; value: T }

export function clientDisplayName(client: Client | null): string {
  if (!client) return 'Cliente'
  return client.clientType === 'PERSON'
    ? client.personalData?.fullName || 'Cliente'
    : client.companyData?.tradingName || client.companyData?.companyName || 'Cliente'
}

function primaryContact(client: Client): { phone?: string; email?: string } {
  const contacts = client.contacts ?? []
  const pick = (type: string) => (contacts.find((c) => c.contactType === type && c.isPrimary) ?? contacts.find((c) => c.contactType === type))?.value
  return { phone: client.whatsappPhone || pick('PHONE') || pick('WHATSAPP'), email: pick('EMAIL') }
}

const byNewest = (list: Interaction[]) =>
  [...list].sort((a, b) => (parseDbTimestamp(b.createdAt)?.getTime() ?? 0) - (parseDbTimestamp(a.createdAt)?.getTime() ?? 0))

/** Bloco do dossiê: título, ação opcional e conteúdo. */
function DossierSection({ title, action, children, className }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section aria-label={title} className={cn('flex min-w-0 flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Bloco recolhido por padrão (atendimentos, histórico). */
function FoldedSection({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-xl border bg-card">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left font-semibold">
        {title}
        <ChevronDown className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-4 py-4">{children}</CollapsibleContent>
    </Collapsible>
  )
}

function SectionError({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      {message}
      <Button size="xs" variant="outline" onClick={retry}>Tentar de novo</Button>
    </div>
  )
}

function InteractionsBlock({ clientId, clientName }: { clientId: string; clientName: string }) {
  const navigate = useNavigate()
  const [interactions, setInteractions] = useState<Load<Interaction[]>>({ state: 'loading' })

  const load = useCallback(async () => {
    setInteractions({ state: 'loading' })
    try {
      setInteractions({ state: 'ready', value: byNewest(await clientService.getClientInteractions(clientId) as Interaction[]) })
    } catch (err) {
      setInteractions({ state: 'error', message: getErrorMessage(err, 'Erro ao carregar os atendimentos') })
    }
  }, [clientId])

  useEffect(() => { void load() }, [load])

  const remove = async (interactionId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este atendimento?')) return
    try {
      await clientService.deleteInteraction(clientId, interactionId)
      await load()
    } catch {
      alert('Erro ao excluir atendimento')
    }
  }

  if (interactions.state === 'loading') return <Skeleton className="h-16" />
  if (interactions.state === 'error') return <SectionError message={interactions.message} retry={load} />

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button size="sm" variant="outline" asChild>
          <Link to={`/interactions/new?clientId=${clientId}`}><Plus className="size-4" /> Novo atendimento</Link>
        </Button>
      </div>
      {interactions.value.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum atendimento registrado para {clientName}.</p>
      ) : (
        <div>
          {interactions.value.map((interaction) => (
            <InteractionTimelineCard
              key={interaction.id}
              interaction={interaction}
              onEdit={() => navigate(`/interactions/${interaction.id}`)}
              onDelete={() => remove(interaction.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function ClientDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [client, setClient] = useState<Load<Client>>({ state: 'loading' })
  const [cases, setCases] = useState<Load<Case[]>>({ state: 'loading' })
  const [taskRefresh, setTaskRefresh] = useState(0)

  const loadClient = useCallback(async (clientId: string) => {
    setClient({ state: 'loading' })
    try {
      const data = await clientService.getClientById(clientId)
      setClient({ state: 'ready', value: data })
      rememberClient({ id: clientId, name: clientDisplayName(data) })
    } catch (err: unknown) {
      if (getErrorStatus(err) === 404 || getErrorMessage(err).includes('não encontrado')) {
        navigate('/clients', { state: { message: 'Cliente não encontrado. Pode ter sido excluído.' } })
        return
      }
      setClient({ state: 'error', message: 'Erro ao carregar o cliente' })
    }
  }, [navigate])

  const loadCases = useCallback(async (clientId: string) => {
    setCases({ state: 'loading' })
    try {
      const data = await caseService.getAllCases({ clientId }, { page: 1, limit: 100, sort: 'createdAt:desc' })
      setCases({ state: 'ready', value: data.cases || [] })
    } catch (err) {
      setCases({ state: 'error', message: getErrorMessage(err, 'Erro ao carregar os casos') })
    }
  }, [])

  useEffect(() => {
    if (!id) return
    void loadClient(id)
    void loadCases(id)
  }, [id, loadClient, loadCases])

  const replaceCase = (updated: Case) =>
    setCases((prev) => prev.state === 'ready'
      ? { state: 'ready', value: prev.value.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)) }
      : prev)

  if (!id) return null

  if (client.state === 'loading') {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-80" />
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    )
  }

  if (client.state === 'error') {
    return (
      <div className="p-4 sm:p-6">
        <SectionError message={client.message} retry={() => loadClient(id)} />
      </div>
    )
  }

  const data = client.value
  const name = clientDisplayName(data)
  const contact = primaryContact(data)
  const ClientIcon = data.clientType === 'COMPANY' ? Building2 : User

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 p-4 sm:p-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ClientIcon className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
            <h1 className="min-w-0 break-words text-2xl font-semibold">{name}</h1>
          </div>
          {(contact.phone || contact.email) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
              {contact.phone && <WhatsAppLink phone={contact.phone} iconSize="sm" />}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="inline-flex min-w-0 items-center gap-1.5 break-all text-primary underline-offset-4 hover:underline">
                  <Mail className="size-4 shrink-0" aria-hidden="true" /> {contact.email}
                </a>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" asChild>
            <Link to={`/cases/new?clientId=${id}`}><Plus className="size-4" /> Novo caso</Link>
          </Button>
          <TaskQuickCreateButton
            context={{ clientId: id, clientName: name, originType: 'manual' }}
            onTaskCreated={() => setTaskRefresh((n) => n + 1)}
            buttonLabel="Nova tarefa"
            className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
          />
          <Button size="sm" variant="ghost" asChild>
            <Link to={`/clients/${id}/edit`}><Edit className="size-4" /> Editar cadastro</Link>
          </Button>
        </div>
      </header>

      <DossierSection title="Casos">
        {cases.state === 'loading' && <Skeleton className="h-24" />}
        {cases.state === 'error' && <SectionError message={cases.message} retry={() => loadCases(id)} />}
        {cases.state === 'ready' && (
          <ClientCaseList cases={cases.value} onCaseChanged={replaceCase} onCreateCase={() => navigate(`/cases/new?clientId=${id}`)} />
        )}
      </DossierSection>

      <DossierSection title="Prazos e tarefas">
        <TaskListByClient clientId={id} refreshTrigger={taskRefresh} compact />
      </DossierSection>

      <FinanceSummaryCard clientId={id} hideWhenEmpty />

      <DossierSection title="Documentos">
        <ClientDocumentsAggregated clientId={id} clientName={name} showStats={false} />
      </DossierSection>

      <div className="flex flex-col gap-3">
        <FoldedSection title="Atendimentos">
          <InteractionsBlock clientId={id} clientName={name} />
        </FoldedSection>
        <FoldedSection title="Histórico">
          <ClientTimeline clientId={id} />
        </FoldedSection>
      </div>

      {data.notes && (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <MessageSquare className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span className="whitespace-pre-wrap">{data.notes}</span>
        </p>
      )}
    </div>
  )
}
